import type { ReactNode } from 'react'

import { styled } from 'styled-components'

import { Colors, Typography } from '@b-stock/fe-core/design-system'

export type CardGridItem = {
  id: string
  isFullWidth?: boolean
  label?: ReactNode
  testId?: string
  value: ReactNode
}

/*
 * Card-body grid sizing. The column count is capped at MAX_COLS and each cell
 * is floored at MIN_COL_WIDTH: on a wide card the per-track minimum resolves to
 * one column's share of the row (full width minus the inter-column gaps,
 * divided by MAX_COLS), so a further column never fits; as the card narrows the
 * floor takes over and the count drops (4 → 3 → 2 → 1).
 */
const MAX_COLS = 4
const COL_GAP_REM = 0.875
const MIN_COL_WIDTH = '9rem'

const Grid = styled.div`
  column-gap: ${COL_GAP_REM}rem;
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      max(
        ${MIN_COL_WIDTH},
        calc((100% - ${(MAX_COLS - 1) * COL_GAP_REM}rem) / ${MAX_COLS})
      ),
      1fr
    )
  );
  padding: 1rem;
  row-gap: 1.5rem;
`

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

const FullWidthCell = styled(Cell)`
  grid-column: 1 / -1;
`

const CellLabel = styled.span`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  text-transform: uppercase;
`

export const CellValue = styled.span`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

export const CellSmallValue = styled.span`
  ${Typography.Subtitle3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

export const SuccessCellValue = styled(CellValue)`
  color: ${Colors.Semantic.Success.copy};
`

export const CellSubValue = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

/**
 * Shared body for V2 listing cards: a responsive grid (≤4 cols, 9rem floor) of
 * label/value cells, plus full-width content blocks. `label` is wrapped in the
 * shared CellLabel; `value` is rendered as-is so callers compose it from the
 * exported value primitives (CellValue / SuccessCellValue / CellSubValue) or
 * their own card-specific styles.
 */
export const CardGrid = ({ items }: { items: CardGridItem[] }) => (
  <Grid>
    {items.map(({ id, label, value, isFullWidth, testId }) => {
      const Wrapper = isFullWidth ? FullWidthCell : Cell
      return (
        <Wrapper key={id} data-testid={testId}>
          {label ? <CellLabel>{label}</CellLabel> : null}
          {value}
        </Wrapper>
      )
    })}
  </Grid>
)
